"use client";

import { SwitcherV2 } from "components/ui/v2";
import { useMutation, useQuery } from "@apollo/client";
import { MaintainerDataTableV2 } from "components/ui/v2/maintainer/maintainer-data-table-v2";
import { IColumn } from "components/ui/v2/maintainer/types";
import { gqlCertificateUser } from "gql";
import { IUpdateCertificateStateByUserInput } from "gql/Certificate-user/mutations";
import { IGetUserCertificatesByCertificateIdResponse } from "gql/Certificate-user/queries";
import { ICertificate } from "gql/Certificates/queries";
import useTranslation from "next-translate/useTranslation";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { getQueryOperator } from "utils/helpers";

interface IUsersAssignationProps {
  certificate?: ICertificate | undefined;
  hideHeader?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

interface IColumnData {
  user: IUserCertificated;
  issueDate: string;
  deleted: boolean;
  id: string;
}

interface IUserCertificated {
  name: string;
  lastname: string;
  email: string;
}

export const UsersAssignation = (props: IUsersAssignationProps) => {
  const { certificate, hideHeader = false, onLoadingChange } = props;
  const { t } = useTranslation("common");
  const { data: assignmentsdData, loading } =
    useQuery<IGetUserCertificatesByCertificateIdResponse>(
      gqlCertificateUser.queries.GET_ASSIGNMENTS,
      {
        variables: { certificateId: certificate?.id },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
      }
    );

  useEffect(() => {
    if (!loading) onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  const [updateCertificateStateByUser] = useMutation<{
    updateCertificateStateByUser: IUpdateCertificateStateByUserInput;
  }>(gqlCertificateUser.mutations.UPDATE_CERTIFICATE_STATE_BY_USER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      getQueryOperator(gqlCertificateUser.queries.GET_ASSIGNMENTS),
    ],
    onCompleted() {
      toast.success(t("Certificado eliminado correctamente"));
    },
    onError() {
      toast.error(t("No se ha podido eliminar el certificado"));
    },
  });

  const handleUpdateCertificateStateByUser = (userData: IColumnData) => {
    if (userData) {
      updateCertificateStateByUser({
        variables: {
          userCertificateId: userData?.id,
        },
      });
    }
  };

  const columns: IColumn<IColumnData>[] = [
    {
      dataKey: "user",
      header: t("Nombre usuario"),
      sortable: true,
      width: 350,
      customCell: ({ rowData }) => (
        <span className="text-left text-[13px] font-medium text-slate-900 dark:text-zinc-50">
          {`${rowData.user.name} ${rowData.user.lastname || ""}`}
        </span>
      ),
    },
    {
      dataKey: "user",
      header: t("Email"),
      sortable: true,
      width: 300,
      customCell: ({ rowData }) => (
        <span className="text-left text-[9px] text-slate-500 dark:text-zinc-400">
          {rowData.user.email}
        </span>
      ),
    },
    {
      dataKey: "issueDate",
      header: t("Fecha de emisión"),
      sortable: true,
      width: 180,
      customCell: ({ rowData }) => (
        <span className="text-left text-[9px] text-slate-500 dark:text-zinc-400">
          {new Date(rowData.issueDate).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      dataKey: "deleted",
      header: t("Activo"),
      sortable: true,
      width: 180,
      customCell: ({ rowData }) => (
        <SwitcherV2
          dense
          aria-label={t("Activo")}
          defaultChecked={!rowData.deleted}
          onCheckedChange={() => handleUpdateCertificateStateByUser(rowData)}
        />
      ),
    },
  ];

  if (loading) {
    return null;
  }

  return (
    <div>
      {!hideHeader ? (
        <p className="mb-3 text-[13px] font-semibold text-slate-900 dark:text-zinc-50">
          {t("Certificados Asignados")}
        </p>
      ) : null}
      <MaintainerDataTableV2
        columns={columns}
        data={assignmentsdData?.getUserCertificatesByCertificateId || []}
      />
    </div>
  );
};
